import React from "react";

import { faChevronRight } from "@fortawesome/pro-solid-svg-icons/faChevronRight";
import { faExclamationCircle } from "@fortawesome/pro-solid-svg-icons/faExclamationCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Col from "antd/es/col";
import Divider from "antd/es/divider";
import Row from "antd/es/row";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import DealStageType from "@mapmycustomers/shared/enum/DealStageType";
import { Deal } from "@mapmycustomers/shared/types/entity";

import layout from "@app/styles/layout";

import styles from "./DealInfo.module.scss";

interface Props {
  deal: Deal;
}

const DealInfo: React.FC<Props> = ({ deal }) => {
  const intl = useIntl();
  const {
    cadenceDaysOut,
    daysInStage,
    dealLossComment,
    dealLossReason,
    outOfCadence,
    rottingDaysOut,
    stage,
  } = deal;
  if (dealLossReason && stage.type === DealStageType.LOST) {
    return (
      <Tooltip
        placement="bottom"
        title={
          <div>
            <div className={styles.lostReason}>{dealLossReason.name}</div>
            {dealLossComment && (
              <>
                <Divider />
                {dealLossComment}
              </>
            )}
          </div>
        }
      >
        <Row className={styles.defaultText} gutter={layout.spacerM}>
          <Col>
            {intl.formatMessage({
              id: "dealCards.card.lossReason",
              defaultMessage: "Reason for loss",
              description: "Deal card loss reason",
            })}
          </Col>
          <Col>
            <FontAwesomeIcon icon={faChevronRight} />
          </Col>
        </Row>
      </Tooltip>
    );
  }
  if (outOfCadence) {
    return (
      <Row className={styles.errorText} gutter={layout.spacerXS}>
        <Col>
          <FontAwesomeIcon icon={faExclamationCircle} />
        </Col>
        <Col>
          {intl.formatMessage(
            {
              id: "dealCards.card.outOfCadence",
              defaultMessage: "{cadenceDaysOut} days over cadence",
              description: "Deal card over cadence text",
            },
            { cadenceDaysOut }
          )}
        </Col>
      </Row>
    );
  }
  if (stage.rottingInterval && (rottingDaysOut || 0) > 0 && daysInStage > stage.rottingInterval) {
    return (
      <Tooltip
        placement="bottom"
        title={intl.formatMessage(
          {
            id: "dealCards.card.daysInStageTooltip",
            defaultMessage: "Rot limit: <b>{rottingInterval} days</b>",
            description: "Deal card over cadence tooltip",
          },
          {
            b: (text) => <div className={styles.infoText}>{text}</div>,
            rottingInterval: stage.rottingInterval,
          }
        )}
      >
        <Row className={styles.infoText} gutter={layout.spacerXS}>
          <Col>
            <FontAwesomeIcon icon={faExclamationCircle} />
          </Col>
          <Col>
            {intl.formatMessage(
              {
                id: "dealCards.card.daysOverCadence",
                defaultMessage: "{count} days in stage",
                description: "Deal card over cadence text",
              },
              { count: daysInStage }
            )}
          </Col>
        </Row>
      </Tooltip>
    );
  }
  return null;
};

export default DealInfo;
